// Wave 3.99af — Saved Filter Views API helpers.
// Thin wrappers around the /api/saved-views endpoints. Every call is scoped
// to a company (Super Admin must pass one; the Orders page always has an
// effective company id in hand).
import { api } from '@/lib/api';

const DEFAULT_CONTEXT = 'orders';

export async function listSavedViews(companyId, context = DEFAULT_CONTEXT) {
  const { data } = await api.get('/saved-views', {
    params: { company_id: companyId, context },
  });
  return Array.isArray(data) ? data : [];
}

export async function createSavedView(companyId, payload) {
  const { data } = await api.post('/saved-views', payload, {
    params: { company_id: companyId },
  });
  return data;
}

export async function updateSavedView(companyId, id, patch) {
  const { data } = await api.patch(`/saved-views/${id}`, patch, {
    params: { company_id: companyId },
  });
  return data;
}

export async function deleteSavedView(companyId, id) {
  const { data } = await api.delete(`/saved-views/${id}`, {
    params: { company_id: companyId },
  });
  return data;
}

// Human-readable label per raw-material slug (mirrors Orders.jsx RAW_MATERIALS).
export const SAVED_VIEW_MATERIAL_LABEL = {
  gold: 'Gold',
  diamond: 'Diamonds',
  color_stone: 'Colour Stones',
  pearl: 'Pearls',
};

const STATUS_LABEL = {
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  open: 'All Open',
  all: 'All',
};

// Wave 4.x — CAD source filter labels (In-House vs External CAD).
const CAD_SOURCE_LABEL = {
  in_house: 'In-House CAD',
  external: 'External CAD',
};

// The exact set of Orders filter keys a saved view snapshots. Kept in one
// place so the menu, the summariser and the "is this view active?" check all
// agree.
export const SNAPSHOT_KEYS = [
  'status', 'goldsmith_id', 'customer_id', 'stage', 'substage', 'search',
  'pending_only', 'pending_materials', 'cad_source', 'has_attachments',
];

// Build a snapshot object from the live Orders filter state.
export function snapshotFromFilter(filter = {}) {
  return {
    status: filter.status ?? 'active',
    goldsmith_id: filter.goldsmith_id ?? '',
    customer_id: filter.customer_id ?? '',
    stage: filter.stage ?? 'all',
    substage: filter.substage ?? 'all',
    search: filter.search ?? '',
    pending_only: !!filter.pending_only,
    pending_materials: Array.isArray(filter.pending_materials) ? filter.pending_materials : [],
    cad_source: filter.cad_source ?? 'all',
    has_attachments: !!filter.has_attachments,
  };
}

// Compact, human-readable one-liner describing what a snapshot filters on.
export function summarizeSnapshot(snap, { goldsmiths = [], customers = [] } = {}) {
  const s = snap || {};
  const parts = [];
  if (s.status && s.status !== 'active') parts.push(STATUS_LABEL[s.status] || s.status);
  if (s.goldsmith_id) {
    const g = (goldsmiths || []).find((x) => x.id === s.goldsmith_id);
    parts.push(`Goldsmith: ${g?.name || 'Selected'}`);
  }
  if (s.customer_id) {
    const c = (customers || []).find((x) => x.id === s.customer_id);
    parts.push(`Customer: ${c?.name || 'Selected'}`);
  }
  if (s.stage && s.stage !== 'all') parts.push(`Stage: ${s.stage}`);
  if (s.substage && s.substage !== 'all') {
    parts.push(`Substage: ${s.substage === '__none__' ? 'None' : s.substage}`);
  }
  if (s.cad_source && s.cad_source !== 'all') {
    parts.push(CAD_SOURCE_LABEL[s.cad_source] || s.cad_source);
  }
  if (s.has_attachments) parts.push('Has attachments');
  if (s.search && String(s.search).trim()) parts.push(`"${String(s.search).trim()}"`);
  const mats = Array.isArray(s.pending_materials) ? s.pending_materials : [];
  if (s.pending_only || mats.length) {
    parts.push(mats.length
      ? `Pending: ${mats.map((m) => SAVED_VIEW_MATERIAL_LABEL[m] || m).join(', ')}`
      : 'Pending');
  }
  return parts.length ? parts.join(' · ') : 'All orders (no filters)';
}

// True when the live filter matches a saved snapshot (used to highlight the
// currently-applied view in the menu). View mode is intentionally ignored.
export function snapshotMatchesFilter(snap, filter) {
  const a = snapshotFromFilter(snap);
  const b = snapshotFromFilter(filter);
  if (a.status !== b.status) return false;
  if (a.goldsmith_id !== b.goldsmith_id) return false;
  if (a.customer_id !== b.customer_id) return false;
  if (a.stage !== b.stage) return false;
  if (a.substage !== b.substage) return false;
  if ((a.cad_source || 'all') !== (b.cad_source || 'all')) return false;
  if (!!a.has_attachments !== !!b.has_attachments) return false;
  if ((a.search || '').trim() !== (b.search || '').trim()) return false;
  if (!!a.pending_only !== !!b.pending_only) return false;
  const am = [...(a.pending_materials || [])].sort().join(',');
  const bm = [...(b.pending_materials || [])].sort().join(',');
  return am === bm;
}
